import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import { useHandlers } from '@sparklayer-io/spark-dash-pages/context/page-context';
import type { ComboboxOption } from '@sparklayer-io/spark-dash-ui/components/ui/combobox/types';
import type { RadioSetOption } from '@sparklayer-io/spark-dash-ui/components/ui/radio-set/types';
import { Alert, Button, Card, Combobox, Form, LearnMoreLink, RadioSet, Skeleton } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { I18N_PAGE, I18N_ROLE_CARD } from '../constants';
import { useSalesAgentGroups } from '../hooks/use-sales-agent-groups';
import { useUpdateCustomerGroups } from '../hooks/use-update-customer-groups';
import { useUpdateCustomerRole } from '../hooks/use-update-customer-role';
import type { Customer, CustomerLookupBy } from '../types';

const SALES_ROLES = ['sales-agent', 'sales-admin'];

function roleOptions(): RadioSetOption<string>[] {
  return [
    {
      value: 'main',
      label: t(`${I18N_ROLE_CARD}.main.label`),
      description: t(`${I18N_ROLE_CARD}.main.description`),
    },
    {
      value: 'sales-agent',
      label: t(`${I18N_ROLE_CARD}.sales_agent.label`),
      description: t(`${I18N_ROLE_CARD}.sales_agent.description`),
    },
    {
      value: 'sales-admin',
      label: t(`${I18N_ROLE_CARD}.sales_admin.label`),
      description: t(`${I18N_ROLE_CARD}.sales_admin.description`),
    },
  ];
}

function isErrorWithStatus(err: unknown, status: number): boolean {
  return err instanceof ApiResponseError && err.status === status;
}

export function CustomerRoleCard({
  customer,
  identifier,
  lookupBy,
  onSaved,
}: {
  customer: Customer;
  /** Must be the identifier `lookupBy` resolved the customer by, or the PATCH will not match. */
  identifier: string;
  lookupBy: CustomerLookupBy;
  onSaved: (customer: Customer) => void;
}) {
  const { notificationHandler } = useHandlers();
  const [role, setRole] = useState<string>(customer.role);
  const salesAgentGroups = customer.sales_agent_groups;
  const isSalesRole = SALES_ROLES.includes(role);

  const {
    data: allSalesAgentGroups,
    isLoading: groupsLoading,
    isError: groupsError,
    refetch: refetchGroups,
  } = useSalesAgentGroups(isSalesRole);

  const updateRole = useUpdateCustomerRole(lookupBy, identifier);
  const updateGroups = useUpdateCustomerGroups(lookupBy, identifier);

  function handleSaveRole() {
    updateRole.mutate(
      { role },
      {
        onSuccess: (updated) => {
          notificationHandler.success(t(`${I18N_PAGE}.notifications.role_updated`));
          onSaved(updated);
        },
        onError: (err) => {
          notificationHandler.error(
            isErrorWithStatus(err, 400)
              ? t(`${I18N_PAGE}.notifications.role_update_limit_error`)
              : t('errors:generic'),
          );
        },
      },
    );
  }

  function handleGroupsChange(groups: string[]) {
    const previous = salesAgentGroups;
    // Applied upwards before the PATCH resolves so the combobox moves on click, reverted on failure.
    onSaved({ ...customer, sales_agent_groups: groups });
    updateGroups.mutate(
      { sales_agent_groups: groups },
      {
        onSuccess: onSaved,
        onError: () => {
          onSaved({ ...customer, sales_agent_groups: previous });
          notificationHandler.error(t('errors:generic'));
        },
      },
    );
  }

  // Selected groups are merged in so a group the server list doesn't carry resolves to a labelled
  // chip rather than a blank one.
  const options: ComboboxOption[] = [
    ...new Set([...(allSalesAgentGroups ?? []), ...salesAgentGroups]),
  ].map((group) => ({
    label: group,
    value: group,
  }));

  return (
    <Card>
      <Card.Header headingLevel="h2">{t(`${I18N_ROLE_CARD}.header`)}</Card.Header>
      <Card.Body>
        <Form gap="sm" onSubmit={handleSaveRole}>
          <RadioSet value={role} options={roleOptions()} onChange={setRole} />
          {isSalesRole && (
            <div className="flex flex-col gap-2">
              {groupsLoading ? (
                <Skeleton />
              ) : groupsError ? (
                <Alert
                  headingLevel="h3"
                  variant="error"
                  title={t(
                    `${I18N_ROLE_CARD}.restrict_sales_agent_groups_access_alert.error.title`,
                  )}
                >
                  <div className="flex flex-col">
                    <span>
                      {t(
                        `${I18N_ROLE_CARD}.restrict_sales_agent_groups_access_alert.error.description`,
                      )}
                    </span>
                    <span className="flex justify-end">
                      <Button
                        onClick={() => void refetchGroups()}
                        icon="refresh"
                        variant="secondary"
                      >
                        {t('buttons:reload')}
                      </Button>
                    </span>
                  </div>
                </Alert>
              ) : (
                <Alert headingLevel="h3">
                  <div className="flex flex-col gap-2">
                    <span>
                      {t(`${I18N_ROLE_CARD}.restrict_sales_agent_groups_access_alert.description`)}{' '}
                      <LearnMoreLink link="https://docs.sparklayer.io/sales-agent-ordering" />
                    </span>
                    <Combobox
                      multiple
                      options={options}
                      value={salesAgentGroups}
                      onChange={handleGroupsChange}
                      placeholder={t(
                        `${I18N_ROLE_CARD}.restrict_sales_agent_groups_access_alert.placeholder`,
                      )}
                    />
                  </div>
                </Alert>
              )}
            </div>
          )}
          <Form.Actions>
            <Button type="submit" loading={updateRole.isPending}>
              {t(`${I18N_PAGE}.actions.save_role`)}
            </Button>
          </Form.Actions>
        </Form>
      </Card.Body>
    </Card>
  );
}
